import React from "react";
import {
  Button,
  Col,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import Request from "../../../helpers/request";
import * as url from "../../../helpers/url_helper";
import NotificationMockup from "./NotificationMockup";
const baseSelectStyles = {
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "white",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    fontWeight: "normal",
  }),

  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),

  placeholder: (provided: any) => ({
    ...provided,
    color: "#6c757d",
    fontWeight: "normal",
  }),

  singleValue: (provided: any) => ({
    ...provided,
    color: "#333",
    fontWeight: "normal",
  }),
};

type NotificationType = "manual" | "template";

type SendNotificationModalProps = {
  isOpen: boolean;
  toggle: () => void;
  activityPayload?: any;
  devicePayload?: any;
  cityPayload?: any;
  appliedFilters?: string[];
  searchValue: string;
};

const SendNotificationModal = ({
  isOpen,
  toggle,
  activityPayload,
  devicePayload,
  cityPayload,
  appliedFilters = [],
  searchValue,
}: SendNotificationModalProps) => {
  const defaultNotifyForm = React.useMemo(
    () => ({
      title: "",
      description: "",
      image: "",
      link: "",
      priority: 10,
      buttonText: "",
      buttonUrl: "",
      notificationType: "manual" as NotificationType,
      templateId: "",
    }),
    []
  );
  const [notifyForm, setNotifyForm] = React.useState(defaultNotifyForm);
  const [notifySending, setNotifySending] = React.useState(false);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [templateOptions, setTemplateOptions] = React.useState<any[]>([]);
  const [selectedTemplateData, setSelectedTemplateData] =
    React.useState<any>(null);
  const [templatesList, setTemplatesList] = React.useState<any[]>([]);
  const token = localStorage.getItem("Authorization");

  const searchTimeout = React.useRef<any>(null);
  const fetchTemplates = React.useCallback(async (searchVal: string = "") => {
    setTemplatesLoading(true);
    try {
      const reqObj = {
        page: 1,
        limit: 50,
        isForERP: false,
        search: searchVal,
      };

      const response = await Request.post(
        `${url.BASE_URL}${url.GET_NOTIFICATION_TEMPLATE}`,
        reqObj
      );

      const list = response?.data?.data?.templates || [];
      setTemplatesList(list);

      const normalized = list.map((tpl: any) => {
        const rawLabel = tpl.name || tpl.headings || tpl.id || "";
        const templateId = tpl.id?.toString() || tpl._id?.toString() || "";
        return {
          value: templateId,
          label:
            typeof rawLabel === "string" ? rawLabel : JSON.stringify(rawLabel),
        };
      });

      setTemplateOptions(normalized);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplateOptions([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // =============================
  // Debounced search handler
  // =============================
  const handleTemplateSearch = (val: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      fetchTemplates(val || "");
    }, 400);
  };

  // =============================
  // Initial load on type change
  // =============================
  React.useEffect(() => {
    if (notifyForm.notificationType === "template") {
      fetchTemplates("");
    }
  }, [notifyForm.notificationType, fetchTemplates]);

  // Update selected template data when templateId changes
  React.useEffect(() => {
    if (notifyForm.templateId && templatesList.length > 0) {
      const template = templatesList.find(
        (tpl: any) =>
          tpl.id?.toString() === notifyForm.templateId?.toString() ||
          tpl._id?.toString() === notifyForm.templateId?.toString()
      );
      if (template) {
        console.log("Selected template data:", template);
        setSelectedTemplateData(template);
      } else {
        console.log(
          "Template not found. templateId:",
          notifyForm.templateId,
          "templatesList:",
          templatesList
        );
        setSelectedTemplateData(null);
      }
    } else {
      setSelectedTemplateData(null);
    }
  }, [notifyForm.templateId, templatesList]);

  // Reset form when drawer closes
  React.useEffect(() => {
    if (!isOpen) {
      setNotifyForm(defaultNotifyForm);
      setTemplateOptions([]);
      setTemplatesList([]);
      setSelectedTemplateData(null);
    }
  }, [isOpen, defaultNotifyForm]);

  const handleNotificationTypeChange = (type: NotificationType) => {
    if (type === "manual") {
      setNotifyForm((prev: any) => ({
        ...prev,
        notificationType: "manual",
        templateId: "",
      }));
    } else {
      setNotifyForm((prev: any) => ({
        ...prev,
        notificationType: "template",
        title: "",
        description: "",
        image: "",
        link: "",
        priority: 10,
        buttonText: "",
        buttonUrl: "",
      }));
    }
  };

  const handleSendNotification = async () => {
    if (notifyForm.notificationType === "manual" && !notifyForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (
      notifyForm.notificationType === "template" &&
      !notifyForm.templateId.trim()
    ) {
      toast.error("Template ID is required");
      return;
    }
    try {
      setNotifySending(true);
      const payload = {
        draw: 1,
        order: [],
        start: 0,
        length: 100,
        search: {
          value: searchValue,
          regex: false,
        },
        filter: appliedFilters?.join(",") || "",
        cityId: cityPayload?.cityId || [],
        activityFilter: activityPayload || undefined,
        deviceFilter: devicePayload || undefined,
        sendNotification: true,
        notificationType: notifyForm.notificationType,
        title:
          notifyForm.notificationType === "manual"
            ? notifyForm.title
            : undefined,
        description:
          notifyForm.notificationType === "manual"
            ? notifyForm.description
            : undefined,
        image:
          notifyForm.notificationType === "manual"
            ? notifyForm.image
            : undefined,
        link:
          notifyForm.notificationType === "manual"
            ? notifyForm.link
            : undefined,
        iosSound:
          notifyForm.notificationType === "manual" ? "default" : undefined,
        android_sound:
          notifyForm.notificationType === "manual" ? "default" : undefined,
        priority:
          notifyForm.notificationType === "manual"
            ? notifyForm.priority
            : undefined,
        buttons:
          notifyForm.notificationType === "manual" && notifyForm.buttonText
            ? [
                {
                  id: "btn1",
                  text: notifyForm.buttonText,
                  url: notifyForm.buttonUrl || notifyForm.link,
                },
              ]
            : [],
        templateId:
          notifyForm.notificationType === "template"
            ? notifyForm.templateId
            : undefined,
      };
      const authHeader =
        token && token.startsWith("Bearer")
          ? token
          : token
          ? `Bearer ${token}`
          : "";
      await axios.post(
        `${url.BASE_URL}${url.SEND_TEMPLATE_NOTIFICATION}`,
        payload,
        authHeader ? { headers: { Authorization: authHeader } } : undefined
      );
      toast.success("Notification sent");
      setNotifyForm(defaultNotifyForm);
      toggle();
    } catch (err) {
      console.error("Error sending notification", err);
      toast.error("Failed to send notification");
    } finally {
      setNotifySending(false);
    }
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: "80%" }}
      className="border-start"
    >
      <OffcanvasHeader
        toggle={toggle}
        className="d-flex align-items-center justify-content-between"
      >
        <span className="fw-semibold">Send Notification</span>
      </OffcanvasHeader>
      <OffcanvasBody>
        <Row>
          {/* LEFT SIDE – FORM (md=8) */}
          <Col md={8}>
            <Row>
              <Col className="mb-3">
                <Label>Notification Type</Label>
                <div className="d-flex" style={{ gap: 8 }}>
                  <Button
                    color={
                      notifyForm.notificationType === "manual"
                        ? "secondary"
                        : "outline-secondary"
                    }
                    size="sm"
                    onClick={() => handleNotificationTypeChange("manual")}
                  >
                    Custom
                  </Button>
                  <Button
                    color={
                      notifyForm.notificationType === "template"
                        ? "secondary"
                        : "outline-secondary"
                    }
                    size="sm"
                    onClick={() => handleNotificationTypeChange("template")}
                  >
                    Template
                  </Button>
                </div>
              </Col>
            </Row>

            {notifyForm.notificationType === "manual" && (
              <>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label>Title</Label>
                    <Input
                      type="text"
                      value={notifyForm.title}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Link</Label>
                    <Input
                      type="text"
                      value={notifyForm.link}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          link: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className="mb-3">
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={notifyForm.description}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label>Image URL</Label>
                    <Input
                      type="text"
                      value={notifyForm.image}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          image: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={3} className="mb-3">
                    <Label>Priority</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={notifyForm.priority}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          priority: Number(e.target.value),
                        }))
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label>Button Text</Label>
                    <Input
                      type="text"
                      value={notifyForm.buttonText}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          buttonText: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Button URL</Label>
                    <Input
                      type="text"
                      value={notifyForm.buttonUrl}
                      onChange={(e) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          buttonUrl: e.target.value,
                        }))
                      }
                    />
                  </Col>
                </Row>
              </>
            )}

            {notifyForm.notificationType === "template" && (
              <>
                <Row>
                  <Col md={12} className="mb-3">
                    <Label>Template</Label>
                    <Select
                      isSearchable
                      options={templateOptions}
                      isLoading={templatesLoading}
                      value={templateOptions.find(
                        (opt) => opt.value === notifyForm.templateId
                      )}
                      onInputChange={(val, action) => {
                        if (action.action === "input-change") {
                          handleTemplateSearch(val);
                        }
                      }}
                      onFocus={() => {
                        if (!templateOptions.length) {
                          fetchTemplates("");
                        }
                      }}
                      onChange={(opt: any) =>
                        setNotifyForm((prev: any) => ({
                          ...prev,
                          templateId: opt?.value || "",
                        }))
                      }
                      placeholder="Select template"
                      menuPortalTarget={document.body}
                      styles={baseSelectStyles}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Col>

          {/* RIGHT SIDE – PREVIEW (md=4) */}
          <Col md={4}>
            <div className="mb-3">
              <Label>Preview</Label>
              {notifyForm.notificationType === "template" &&
              selectedTemplateData ? (
                <NotificationMockup templateData={selectedTemplateData} />
              ) : notifyForm.notificationType === "manual" ? (
                <NotificationMockup
                  templateData={{
                    headings: notifyForm.title || "Title",
                    contents: notifyForm.description || "content",
                    global_image: notifyForm.image || null,
                  }}
                />
              ) : (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#999",
                    background: "#f5f5f7",
                    borderRadius: "8px",
                  }}
                >
                  {notifyForm.notificationType === "template"
                    ? "Select a template to preview"
                    : "Fill in the form to see preview"}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </OffcanvasBody>
      <div
        className="d-flex justify-content-end border-top"
        style={{ gap: 8, padding: "12px 16px" }}
      >
        <Button color="secondary" onClick={toggle} size="sm">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={notifySending}
          onClick={handleSendNotification}
          size="sm"
        >
          {notifySending && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          Send
        </Button>
      </div>
    </Offcanvas>
  );
};

export default SendNotificationModal;
